import { action, observable } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IEventHubsDAO, HubConfigurationSettingsType, IHubBrandingDTO } from '../../../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../../../api/implementations/event-hubs.dao';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { RGB_COLOR_REG_EXP } from '../../../../../../constants/regex.constants';
import { LONG_RICH_TEXT_LENGTH } from '../../../../../../constants/common.constants';
import { CUSTOM_CSS_LONG_ERROR_MESSAGE } from '../../../../../../constants/validation-errors';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import UploadImageS3VM from '../../../../../_events/_manage-event/shared/upload-image/upload-image-to-s3.viewmodel';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import {
    HUB_SETTINGS_BRANDING_OPTION_TEXT,
    HUB_SETTINGS_FOOTER_OPTION_TEXT,
    HUB_MENU_TEXT,
    HUB_SETTINGS_REGISTRATION_OPTION_TEXT,
    HUB_SIGN_IN_TEXT,
} from '../../../../event-hubs.text';
import { HubRegistrationSettingsVM } from './_registration/hub-registration-settings.viewmodel';
import { HubSignInSettingsVM } from './_sign-in/hub-sign-in-settings.viewmodel';
import { HubFooterSettingsVM } from './_footer/hub-footer-settings.viewmodel';
import { HubMenuSettingsVM } from './_menu/hub-menu-settings.viewmodel';
import { EditHubSettingType } from './edit-hub-settings-type';
import { HubRegistrationConfigurationDAO } from '../../../../../../api/implementations/hub-registration-configuration.dao';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { HubPreviewRefreshCallback } from '../../../_content/_hub-embed/hub-preview-sync.types';
import { buildBrandingPreviewExpectation } from '../../../_content/_hub-embed/hub-preview-sync.expectations';

const HUB_HEX_COLOR_REG_EXP = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;

interface IBrandingImageSnapshot {
    assetUrl: string;
    title: string;
    objectKey: string;
}

const emptyImageSnapshot = (): IBrandingImageSnapshot => ({
    assetUrl: '',
    title: '',
    objectKey: '',
});

export class EditHubVM {
    @observable
    public uploadLogoImageS3VM: UploadImageS3VM;

    @observable
    public uploadBackgroundImageS3VM: UploadImageS3VM;

    @observable
    public registrationVM: HubRegistrationSettingsVM | null = null;

    @observable
    public signInVM: HubSignInSettingsVM | null = null;

    @observable
    public footerVM: HubFooterSettingsVM | null = null;

    @observable
    public menuVM: HubMenuSettingsVM | null = null;

    @observable
    private selectedSetting: EditHubSettingType;
    @observable
    private primaryColor = '';
    @observable
    private primaryColorSnapshot = '';
    @observable
    private customCss = '';
    @observable
    private customCssSnapshot = '';
    @observable
    private customCssDraft = '';
    @observable
    private customCssEditorOpen = false;
    @observable
    private loading = false;
    @observable
    private saving = false;

    private logoImageSnapshot: IBrandingImageSnapshot = emptyImageSnapshot();
    private backgroundImageSnapshot: IBrandingImageSnapshot = emptyImageSnapshot();

    constructor(
        private readonly accountId: number,
        private readonly eventHubId: number,
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly intlProvider: IReactIntlProvider = Container.get(IReactIntlProvider),
        private readonly onHubPreviewRefresh?: HubPreviewRefreshCallback,
        private readonly hubVersionId?: number,
    ) {
        this.uploadLogoImageS3VM = new UploadImageS3VM(this.accountId, this.eventHubId, 'logoImage', 'eventHub');
        this.uploadBackgroundImageS3VM = new UploadImageS3VM(
            this.accountId,
            this.eventHubId,
            'backgroundImage',
            'eventHub'
        );
        if (this.canConfigureRegistrationAndSignIn()) {
            this.registrationVM = new HubRegistrationSettingsVM(
                this.accountId,
                this.eventHubId,
                this.eventHubsDAO,
                Container.get(HubRegistrationConfigurationDAO),
                this.intlProvider,
                Container.get(TrackingServiceProvider),
                this.onHubPreviewRefresh,
            );
            this.signInVM = new HubSignInSettingsVM(
                this.accountId,
                this.eventHubId,
                this.eventHubsDAO,
                this.intlProvider,
                Container.get(TrackingServiceProvider),
                this.onHubPreviewRefresh,
            );
        }
        if (this.canManageFooter()) {
            this.footerVM = new HubFooterSettingsVM(
                this.accountId,
                this.eventHubId,
                this.eventHubsDAO,
                this.intlProvider,
                this.onHubPreviewRefresh,
            );
        }
        if (this.canManageMenu() && this.hubVersionId != null) {
            this.menuVM = new HubMenuSettingsVM(
                this.hubVersionId,
                this.eventHubsDAO,
                this.intlProvider,
            );
        }
        this.selectedSetting = this.getDefaultSettingType();
        void this.loadBrandingSettings();
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public canManageBranding(): boolean {
        return hasPermission(Permission.MANAGE_HUB_BRANDING);
    }

    public canConfigureRegistrationAndSignIn(): boolean {
        return hasPermission(Permission.CONFIGURE_EVENT_HUB_REGISTRATION_AND_SIGN_IN);
    }

    public canManageFooter(): boolean {
        return hasPermission(Permission.HUB_MANAGE_PAGES_AND_MODULES);
    }

    public canManageMenu(): boolean {
        return hasPermission(Permission.HUB_MANAGE_PAGES_AND_MODULES);
    }

    public getSelectedSetting(): EditHubSettingType {
        return this.selectedSetting;
    }

    private getActiveSubVM(): HubRegistrationSettingsVM | HubSignInSettingsVM | HubFooterSettingsVM | HubMenuSettingsVM | null {
        switch (this.selectedSetting) {
            case EditHubSettingType.REGISTRATION:
                return this.registrationVM;
            case EditHubSettingType.SIGN_IN:
                return this.signInVM;
            case EditHubSettingType.FOOTER:
                return this.footerVM;
            case EditHubSettingType.MENU:
                return this.menuVM;
            default:
                return null;
        }
    }

    public isLoading(): boolean {
        const activeSubVM = this.getActiveSubVM();
        if (activeSubVM) {
            return activeSubVM.isLoading();
        }
        return this.loading;
    }

    public isSaving(): boolean {
        const activeSubVM = this.getActiveSubVM();
        if (activeSubVM) {
            return activeSubVM.isSaving();
        }
        return this.saving;
    }

    public getPrimaryColor(): string {
        return this.primaryColor;
    }

    public hasCustomCssApplied(): boolean {
        return !!this.customCss;
    }

    public isCustomCssEditorOpen(): boolean {
        return this.customCssEditorOpen;
    }

    public getCustomCssDraft(): string {
        return this.customCssDraft;
    }

    public getCustomCssDraftValidationError(): string | null {
        return EditHubVM.validateCustomCssLength(this.customCssDraft);
    }

    public hasUnsavedChanges(): boolean {
        if (this.selectedSetting === EditHubSettingType.REGISTRATION) {
            return this.registrationVM?.hasUnsavedChanges() ?? false;
        }

        if (this.selectedSetting === EditHubSettingType.SIGN_IN) {
            return this.signInVM?.hasUnsavedChanges() ?? false;
        }

        if (this.selectedSetting === EditHubSettingType.FOOTER) {
            return this.footerVM?.hasUnsavedChanges() ?? false;
        }

        return this.primaryColorSnapshot !== this.primaryColor
            || this.customCssSnapshot !== this.customCss
            || !!this.uploadLogoImageS3VM.getImageChanged
            || !!this.uploadBackgroundImageS3VM.getImageChanged;
    }

    public isImageUploading(): boolean {
        return this.uploadLogoImageS3VM.isImageUploading
            || this.uploadBackgroundImageS3VM.isImageUploading;
    }

    public isSaveButtonDisabled(): boolean {
        const activeSubVM = this.getActiveSubVM();
        if (activeSubVM) {
            return activeSubVM.isSaveButtonDisabled();
        }
        return this.loading || this.saving || this.isImageUploading();
    }

    public isCancelButtonDisabled(): boolean {
        const activeSubVM = this.getActiveSubVM();
        if (activeSubVM) {
            return activeSubVM.isCancelButtonDisabled();
        }
        return this.loading || this.saving || !this.hasUnsavedChanges();
    }

    @action
    public selectSetting = (setting: EditHubSettingType): void => {
        if (!this.getAvailableSettingOptions().some(option => option.value === setting)) {
            return;
        }

        this.selectedSetting = setting;
    };

    @action
    public setPrimaryColor = (value: string): void => {
        this.primaryColor = value;
    };

    @action
    public openCustomCssEditor = (): void => {
        this.customCssDraft = this.customCss;
        this.customCssEditorOpen = true;
    };

    @action
    public closeCustomCssEditor = (): void => {
        this.customCssEditorOpen = false;
    };

    @action
    public setCustomCssDraft = (value: string): void => {
        this.customCssDraft = value;
    };

    @action
    public applyCustomCssEditor = (): boolean => {
        if (this.getCustomCssDraftValidationError()) {
            return false;
        }

        this.customCss = this.customCssDraft.trim();
        this.closeCustomCssEditor();
        return true;
    };

    @action
    public onCancel = (): void => {
        const activeSubVM = this.getActiveSubVM();
        if (activeSubVM) {
            activeSubVM.onCancel();
            return;
        }

        this.primaryColor = this.primaryColorSnapshot;
        this.customCss = this.customCssSnapshot;
        this.restoreBrandingImagesFromSnapshots();
    };

    public onSaveChanges = (): void => {
        const activeSubVM = this.getActiveSubVM();
        if (activeSubVM) {
            activeSubVM.onSaveChanges();
            return;
        }

        void this.saveBrandingSettings();
    };

    public getNormalizedPrimaryColor(): string | null {
        if (!this.getColorValidationError()) {
            const rgbMatch = this.primaryColor.match(RGB_COLOR_REG_EXP);
            if (rgbMatch) {
                const [, red, green, blue] = rgbMatch;
                return `#${[red, green, blue]
                    .map((channel: string) => parseInt(channel, 10).toString(16).padStart(2, '0'))
                    .join('')}`;
            }
            return this.primaryColor;
        }
        return null;
    }

    public getColorValidationError(): string | null {
        if (this.primaryColor && !(this.primaryColor.match(RGB_COLOR_REG_EXP) || this.primaryColor.match(HUB_HEX_COLOR_REG_EXP))) {
            return this.intl.formatMessage({
                description: 'Warning message',
                defaultMessage: 'Primary color should be in HEX - #000000 or RGB - rgb(0, 0, 255) format',
                id: 'oe.common.validationPrimaryColor'
            });
        }
        return null;
    }

    public getAvailableSettingOptions(): { label: string; value: EditHubSettingType }[] {
        const options: { label: string; value: EditHubSettingType }[] = [];

        if (this.canManageBranding()) {
            options.push({
                label: this.intl.formatMessage(HUB_SETTINGS_BRANDING_OPTION_TEXT),
                value: EditHubSettingType.BRANDING,
            });
        }

        if (this.canConfigureRegistrationAndSignIn()) {
            options.push({
                label: this.intl.formatMessage(HUB_SETTINGS_REGISTRATION_OPTION_TEXT),
                value: EditHubSettingType.REGISTRATION,
            });
            options.push({
                label: this.intl.formatMessage(HUB_SIGN_IN_TEXT),
                value: EditHubSettingType.SIGN_IN,
            });
        }

        if (this.canManageFooter()) {
            options.push({
                label: this.intl.formatMessage(HUB_SETTINGS_FOOTER_OPTION_TEXT),
                value: EditHubSettingType.FOOTER,
            });
        }

        if (this.canManageMenu() && this.hubVersionId != null) {
            options.push({
                label: this.intl.formatMessage(HUB_MENU_TEXT),
                value: EditHubSettingType.MENU,
            });
        }

        return options;
    }

    @action
    private setLoading = (loading: boolean): void => {
        this.loading = loading;
    };

    @action
    private setSaving = (saving: boolean): void => {
        this.saving = saving;
    };

    @action
    private setLoadedColor = (color: string | null): void => {
        const loadedColor = color || '';
        this.primaryColor = loadedColor;
        this.primaryColorSnapshot = loadedColor;
    };

    @action
    private setLoadedCustomCss = (customCss: string | null): void => {
        const loadedCustomCss = (customCss ?? '').trim();
        this.customCss = loadedCustomCss;
        this.customCssSnapshot = loadedCustomCss;
    };

    private static validateCustomCssLength(value: string): string | null {
        return value.length > LONG_RICH_TEXT_LENGTH ? CUSTOM_CSS_LONG_ERROR_MESSAGE : null;
    }

    @action
    private applyBrandingImages = (brandingSettings?: IHubBrandingDTO): void => {
        const logoImage = brandingSettings?.logoImage;
        const logoImageWithUrl = brandingSettings?.logoImageWithUrl;
        const backgroundImage = brandingSettings?.backgroundImage;
        const backgroundImageWithUrl = brandingSettings?.backgroundImageWithUrl;

        this.uploadLogoImageS3VM.addImage(
            logoImageWithUrl?.assetUrl,
            logoImage?.title,
            logoImage?.objectKey,
        );
        this.uploadBackgroundImageS3VM.addImage(
            backgroundImageWithUrl?.assetUrl,
            backgroundImage?.title,
            backgroundImage?.objectKey,
        );
        this.uploadLogoImageS3VM.setImageChanged(undefined);
        this.uploadBackgroundImageS3VM.setImageChanged(undefined);

        this.logoImageSnapshot = {
            assetUrl: logoImageWithUrl?.assetUrl ?? '',
            title: logoImage?.title ?? '',
            objectKey: logoImage?.objectKey ?? '',
        };
        this.backgroundImageSnapshot = {
            assetUrl: backgroundImageWithUrl?.assetUrl ?? '',
            title: backgroundImage?.title ?? '',
            objectKey: backgroundImage?.objectKey ?? '',
        };
    };

    @action
    private restoreBrandingImagesFromSnapshots = (): void => {
        this.uploadLogoImageS3VM.addImage(
            this.logoImageSnapshot.assetUrl,
            this.logoImageSnapshot.title,
            this.logoImageSnapshot.objectKey,
        );
        this.uploadBackgroundImageS3VM.addImage(
            this.backgroundImageSnapshot.assetUrl,
            this.backgroundImageSnapshot.title,
            this.backgroundImageSnapshot.objectKey,
        );
        this.uploadLogoImageS3VM.setImageChanged(undefined);
        this.uploadBackgroundImageS3VM.setImageChanged(undefined);
    };

    private getDefaultSettingType(): EditHubSettingType {
        if (this.canManageBranding()) {
            return EditHubSettingType.BRANDING;
        }

        if (this.canConfigureRegistrationAndSignIn()) {
            return EditHubSettingType.REGISTRATION;
        }

        if (this.canManageFooter()) {
            return EditHubSettingType.FOOTER;
        }

        return EditHubSettingType.BRANDING;
    }

    private async loadBrandingSettings(): Promise<void> {
        if (!this.canManageBranding()) {
            return;
        }

        this.setLoading(true);
        try {
            const configs = await this.eventHubsDAO.getEventHubConfigs(this.accountId, this.eventHubId);
            const brandingSettings = configs.find(
                config => config.type === HubConfigurationSettingsType.BRANDING
            ) as IHubBrandingDTO | undefined;
            this.setLoadedColor(brandingSettings?.primaryColor ?? null);
            this.setLoadedCustomCss(brandingSettings?.customCss ?? null);
            this.applyBrandingImages(brandingSettings);
        } catch (error) {
            this.handleHubApiException(error);
        } finally {
            this.setLoading(false);
        }
    }

    private async saveBrandingSettings(): Promise<void> {
        if (this.loading || this.saving || this.getColorValidationError() || !this.hasUnsavedChanges()) {
            return;
        }

        this.setSaving(true);
        const refreshGeneration = this.onHubPreviewRefresh?.beginLoading();
        try {
            const trimmedColor = this.primaryColor.trim();
            const primaryColorToSave = trimmedColor ? trimmedColor : null;
            const trimmedCustomCss = this.customCss.trim();
            const customCssToSave = trimmedCustomCss ? trimmedCustomCss : null;
            const payload: IHubBrandingDTO = {
                accountId: this.accountId,
                eventHubId: this.eventHubId,
                type: HubConfigurationSettingsType.BRANDING,
                primaryColor: primaryColorToSave,
                customCss: customCssToSave,
                ...this.uploadLogoImageS3VM.getImageToApi(),
                ...this.uploadBackgroundImageS3VM.getImageToApi(),
            };
            const updatedSettings = await this.eventHubsDAO.setEventHubConfig(payload) as IHubBrandingDTO;
            this.setLoadedColor(updatedSettings?.primaryColor ?? null);
            this.setLoadedCustomCss(updatedSettings?.customCss ?? null);
            this.applyBrandingImages(updatedSettings);
            void this.onHubPreviewRefresh?.refresh(
                buildBrandingPreviewExpectation(updatedSettings),
                refreshGeneration,
            );
        } catch (error) {
            if (refreshGeneration != null) {
                this.onHubPreviewRefresh?.cancelLoading(refreshGeneration);
            }
            this.handleHubApiException(error);
        } finally {
            this.setSaving(false);
        }
    }

    private handleHubApiException(error: Error): void {
        const errorParsingService = Container.get(IErrorParsing);
        const apiError = errorParsingService.extractAPIError(error);
        const title = apiError?.errorCode === 403 ? 'Access Denied' : 'Error';
        alertServerError(title, errorParsingService.extractMessage(error));
    }
}
